/* eslint-disable max-lines */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { createInventoryApi, listProductNdcsApi } from 'api/inventory';
import { listCatalogApi } from 'api/drug';
import { brandNameAllowedCharRegex, brandNameAllowedValueRegex } from 'common/constants/regex';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import titleCase from 'common/utils/titleCase';
import ManufacturerSelect from './ManufacturerSelect';
import SetDefaultNdcToggle from './SetDefaultNdcToggle';

const REORDER_MODE = {
  DEFAULT: 'default',
  CUSTOM: 'custom',
};

// Mirrors AddStockDialog.js's NDC_REGEX/isValidNdc - same accepted formats
// (10-11 digit, or hyphenated 4-5/3-4/1-2) across every NDC input in this module.
const NDC_REGEX = /^\d{10,11}$|^(\d{4,5}-\d{3,4}-\d{1,2})$/;
const isValidNdc = (value) => NDC_REGEX.test(String(value).trim());

// Non-empty but malformed: show live, since Create item is disabled while ndcValid
// is false and would otherwise never be clicked to set `submitted`.
const getNdcHelperText = (ndc, ndcValid, submitted) => {
  if (ndc.trim().length > 0 && !ndcValid) return 'Invalid NDC format';
  if (submitted && ndc.trim().length === 0) return 'Required';
  return '';
};

const AddInventoryItemDialog = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [catalogOptions, setCatalogOptions] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const catalogTimerRef = useRef(null);

  const [drugName, setDrugName] = useState('');
  const [strength, setStrength] = useState('');
  const [ndc, setNdc] = useState('');

  // POM-5910 — manufacturer is a pick-list rather than free text; the options
  // and their loading state live in ManufacturerSelect. `null` (not '') is the
  // empty value so the MUI Autocomplete stays controlled.
  const [manufacturer, setManufacturer] = useState(null);
  const [reorderMode, setReorderMode] = useState(REORDER_MODE.DEFAULT);
  const [lowStock, setLowStock] = useState('');
  const [criticalStock, setCriticalStock] = useState('');
  const [leadTime, setLeadTime] = useState('');

  // POM-5707 — a brand new NDC has no facility stock by definition, so it cannot
  // be promoted from the inventory grid's Edit Default NDC dialog until someone
  // receives stock against it. Offering the choice at creation time closes that
  // gap; listProductNdcs tells us which NDC (if any) currently holds the flag.
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [currentDefault, setCurrentDefault] = useState(null);
  const [defaultLookupDone, setDefaultLookupDone] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (open) {
      setCatalogOptions([]);
      setSelectedCatalog(null);
      setDrugName('');
      setStrength('');
      setManufacturer(null);
      setNdc('');
      setReorderMode(REORDER_MODE.DEFAULT);
      setLowStock('');
      setCriticalStock('');
      setLeadTime('');
      setSetAsDefault(false);
      setCurrentDefault(null);
      setDefaultLookupDone(false);
      setSubmitted(false);
      setSubmitError('');
    }
  }, [open]);

  const catalogProductId = selectedCatalog?.productId ?? null;

  useEffect(() => {
    if (!open || !catalogProductId) return undefined;

    let cancelled = false;
    setDefaultLookupDone(false);

    listProductNdcsApi(catalogProductId)
      .then((options) => {
        if (cancelled) return;
        setCurrentDefault(options.find((option) => option.isDefault) ?? null);
      })
      .catch(() => {
        // A failed lookup only costs the "replaces X" hint — the checkbox still
        // works, and the backend clears any existing default transactionally.
        if (!cancelled) setCurrentDefault(null);
      })
      .finally(() => {
        if (!cancelled) setDefaultLookupDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [open, catalogProductId]);

  const handleCatalogInputChange = (_, inputVal, reason) => {
    if (reason !== 'input') return;
    clearTimeout(catalogTimerRef.current);
    if (inputVal.trim().length < 3) {
      setCatalogLoading(false);
      setCatalogOptions([]);
      return;
    }
    setCatalogLoading(true);
    catalogTimerRef.current = setTimeout(() => {
      listCatalogApi({ drugName: inputVal.trim() })
        .then((response) => {
          const flattened = (response?.results ?? []).flatMap((drug) =>
            (drug.variants ?? []).map((variant) => ({
              catalogId: variant.catalogId,
              productId: variant.productId,
              drugName: drug.drugName,
              strength: variant.strength,
              displayName: variant.displayName,
              packSizeValue: variant.packSizeValue,
              packSizeUnit: variant.packSizeUnit,
            }))
          );
          setCatalogOptions(flattened);
        })
        .catch(() => {
          // A failed search reads as "no matches" rather than an unhandled rejection; the user
          // can retype to retry, and Add Catalog Item is still available.
          setCatalogOptions([]);
        })
        .finally(() => setCatalogLoading(false));
    }, 300);
  };

  const handleSelectCatalog = (option) => {
    setSelectedCatalog(option);
    const packSize =
      option?.packSizeValue && option?.packSizeUnit
        ? `${option.packSizeValue}${option.packSizeUnit}`
        : '';
    setStrength([option?.strength, packSize].filter(Boolean).join(' '));
    setCatalogOptions([]);
    // The default flag is per product — switching products invalidates both the
    // choice and the "replaces X" hint.
    setSetAsDefault(false);
    setCurrentDefault(null);
  };

  const lowNum = parseFloat(lowStock);
  const criticalNum = parseFloat(criticalStock);
  const leadNum = parseFloat(leadTime);

  const lowValid = lowStock !== '' && !Number.isNaN(lowNum) && lowNum > 0 && lowNum <= 100;
  const leadValid = leadTime !== '' && !Number.isNaN(leadNum) && leadNum >= 0;
  const criticalProvided = criticalStock !== '';
  const criticalValid =
    !criticalProvided ||
    (!Number.isNaN(criticalNum) && criticalNum >= 0 && (!lowValid || criticalNum <= lowNum));

  const reorderValid =
    reorderMode === REORDER_MODE.DEFAULT || (lowValid && leadValid && criticalValid);

  const ndcValid = isValidNdc(ndc);
  const drugNameValid = brandNameAllowedValueRegex.test(drugName);

  const canSave =
    Boolean(selectedCatalog) &&
    drugName.trim().length > 0 &&
    drugNameValid &&
    strength.trim().length > 0 &&
    Boolean(manufacturer) &&
    ndcValid &&
    reorderValid;

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;
    setSaving(true);
    setSubmitError('');

    const input = {
      catalogId: selectedCatalog.catalogId,
      drugName: drugName.trim(),
      strength: strength.trim(),
      // Sent as a plain String exactly as before — the dropdown constrains the
      // input, it does not change how the value is stored.
      manufacturer,
      ndc: ndc.trim(),
      ...(setAsDefault ? { setAsDefault: true } : {}),
      ...(reorderMode === REORDER_MODE.CUSTOM
        ? {
            reorderConfig: {
              lowStockPercent: lowNum,
              leadTime: leadNum,
              ...(criticalProvided ? { criticalStockPercent: criticalNum } : {}),
            },
          }
        : {}),
    };

    createInventoryApi(input)
      .then((createdItem) => {
        setSaving(false);
        enqueueSnackbar(
          setAsDefault
            ? `Inventory item "${drugName}" created and set as the default NDC`
            : `Inventory item "${drugName}" created`,
          { variant: 'success' }
        );
        onCreated(createdItem);
        handleClose();
      })
      .catch((error) => {
        setSaving(false);
        // Surface the backend validation error inline; keep the form open and
        // editable so the user can correct the input and resubmit (POM-5739).
        setSubmitError(getGraphQLErrorMessage(error));
      });
  };

  // Block backdrop-click dismissal, and prevent any close (incl. Escape) while a
  // create is in flight — the only way out is the Cancel button (POM-5739).
  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          Add Inventory Item
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Create a new inventory item and link it to a catalog drug identity.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {submitError && (
          <Alert severity='error' onClose={() => setSubmitError('')} sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box
          sx={{
            p: 1.5,
            mb: 3,
            bgcolor: 'action.hover',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant='caption'
            sx={{ fontWeight: 600, display: 'block', mb: 1.5, color: 'text.secondary' }}
          >
            CATALOG ASSOCIATION
          </Typography>

          <Autocomplete
            size='small'
            fullWidth
            loading={catalogLoading}
            options={catalogOptions}
            filterOptions={(x) => x}
            value={selectedCatalog}
            isOptionEqualToValue={(opt, val) => opt.catalogId === val?.catalogId}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : titleCase(opt.displayName ?? '')
            }
            onInputChange={handleCatalogInputChange}
            onChange={(_, option) => handleSelectCatalog(option)}
            renderOption={(props, opt) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={opt.catalogId}>
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {titleCase(opt.displayName)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Product ID: {opt.productId ?? '—'}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label='Search product by drug name *'
                placeholder='e.g. Paracetamol, Amoxicillin...'
                error={submitted && !selectedCatalog}
                helperText={submitted && !selectedCatalog ? 'Select a product to continue' : ''}
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {catalogLoading && <CircularProgress color='inherit' size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* POM-5910 — the Product ID is only visible in the search dropdown,
              which closes on selection. Echo it back so the user can confirm
              which catalog identity the new NDC is being bound to. */}
          {selectedCatalog && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mt: 1 }}
              data-testid='selected-catalog-product-id'
            >
              Product ID: {selectedCatalog.productId ?? '—'}
            </Typography>
          )}
        </Box>

        {selectedCatalog && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size='small'
              fullWidth
              label='Drug Name (brand) *'
              placeholder='e.g. Dolo'
              value={drugName}
              onChange={(e) => setDrugName(e.target.value)}
              inputProps={{
                onKeyDown: (e) => {
                  if (e.key.length === 1 && !brandNameAllowedCharRegex.test(e.key)) {
                    e.preventDefault();
                  }
                },
              }}
              error={(submitted && drugName.trim().length === 0) || (submitted && !drugNameValid)}
              helperText={
                submitted && !drugNameValid
                  ? 'Must be alphanumeric / - / _ / . / () / \\ / / / +'
                  : 'Brand name for this stocked item. Different brands can share the same product identity.'
              }
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size='small'
                fullWidth
                label='Strength *'
                placeholder='e.g. 250 mg'
                value={strength}
                onChange={(e) => setStrength(e.target.value)}
                error={submitted && strength.trim().length === 0}
              />
              <ManufacturerSelect
                value={manufacturer}
                onChange={setManufacturer}
                showRequiredError={submitted && !manufacturer}
              />
            </Box>

            <TextField
              size='small'
              fullWidth
              label='NDC *'
              value={ndc}
              onChange={(e) => setNdc(e.target.value)}
              error={ndc.trim().length > 0 ? !ndcValid : submitted}
              helperText={getNdcHelperText(ndc, ndcValid, submitted)}
            />

            {catalogProductId && (
              <SetDefaultNdcToggle
                productId={catalogProductId}
                currentDefault={currentDefault}
                lookupDone={defaultLookupDone}
                checked={setAsDefault}
                onChange={setSetAsDefault}
              />
            )}

            <Box>
              <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                Reorder Config (optional)
              </Typography>
              <RadioGroup
                row
                value={reorderMode}
                onChange={(e) => setReorderMode(e.target.value)}
                sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}
              >
                {[
                  { value: REORDER_MODE.DEFAULT, label: 'Use system default' },
                  { value: REORDER_MODE.CUSTOM, label: 'Custom threshold' },
                ].map((option) => (
                  <Box
                    key={option.value}
                    sx={{
                      flex: 1,
                      border: '1px solid',
                      borderColor: reorderMode === option.value ? 'primary.main' : 'divider',
                      bgcolor: reorderMode === option.value ? 'action.selected' : 'transparent',
                      borderRadius: 1,
                      px: 1,
                    }}
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio size='small' />}
                      label={
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {option.label}
                        </Typography>
                      }
                      sx={{ m: 0, width: '100%', py: 0.5 }}
                    />
                  </Box>
                ))}
              </RadioGroup>
            </Box>

            {reorderMode === REORDER_MODE.CUSTOM && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label='Low Stock % *'
                  placeholder='e.g. 50'
                  value={lowStock}
                  onChange={(e) => setLowStock(e.target.value)}
                  error={submitted && !lowValid}
                />
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label='Critical Stock %'
                  placeholder='e.g. 10'
                  value={criticalStock}
                  onChange={(e) => setCriticalStock(e.target.value)}
                  error={criticalProvided && !criticalValid}
                />
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label='Lead Time (days) *'
                  placeholder='e.g. 14'
                  value={leadTime}
                  onChange={(e) => setLeadTime(e.target.value)}
                  error={submitted && !leadValid}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={handleClose} color='inherit' disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={!canSave || saving}>
          {saving ? 'Creating…' : 'Create item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddInventoryItemDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
AddInventoryItemDialog.defaultProps = {
  onCreated: () => {},
};

export default AddInventoryItemDialog;
